import { useRef, useState, useEffect, useMemo, Suspense } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faClock,
  faSignal,
  faSun,
  faMoon,
} from '@fortawesome/free-solid-svg-icons';
import Prism from 'prismjs';
import 'prismjs/components/prism-javascript';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import {
  getArticle as getStaticArticle,
  getPathBySlug,
  getRelatedArticles as getStaticRelated,
  isArticlePublished,
} from './content';
import { publicArticlesApi } from '../../services/api/articlesApi';
import type { Article } from './types';
import { mockRegistry } from './mocks';
import { usePageMeta } from '../../hooks/usePageMeta';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(18px)',
    transition: 'opacity 0.7s ease, transform 0.7s ease',
  };
  return { ref, style };
}

const difficultyLabel: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const difficultyColor: Record<string, string> = {
  beginner: 'text-emerald-400',
  intermediate: 'text-blue-400',
  advanced: 'text-amber-400',
};

export default function LearnArticlePage() {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const path = category ? getPathBySlug(category) : undefined;
  const staticArticle = slug ? getStaticArticle(slug) : undefined;

  const [article, setArticle] = useState<Article | undefined>(staticArticle);
  const [loading, setLoading] = useState(!staticArticle);

  // Fetch from API, fallback to static
  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let cancelled = false;
    publicArticlesApi.get(slug)
      .then((res) => {
        if (!cancelled && res.success) {
          const merged = { ...res.article };
          // Static content is the source of truth for titles and sections
          if (staticArticle) {
            if (staticArticle.subtitle) {
              merged.title = staticArticle.title;
              merged.subtitle = staticArticle.subtitle;
            }
            if (staticArticle.sections?.length) {
              merged.sections = staticArticle.sections;
            }
          }
          setArticle(merged);
        }
      })
      .catch(() => { /* keep static fallback */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  usePageMeta({
    title: article ? (article.subtitle ? `${article.title}: ${article.subtitle}` : article.title) : 'Learn',
    description: article ? article.description : '',
  });
  const revHero = useScrollReveal(0.1);
  const revCta = useScrollReveal(0.1);

  const [activeSection, setActiveSection] = useState<string>('');
  const [lightMode, setLightMode] = useState<boolean>(() => {
    try { return localStorage.getItem('article-light-mode') === 'true'; } catch { return false; }
  });
  const articleRef = useRef<HTMLElement>(null);

  // Pre-highlight code blocks in HTML strings so re-renders preserve syntax coloring
  const highlightedSections = useMemo(() => {
    if (!article) return [];
    const grammar = Prism.languages.javascript;
    if (!grammar) return article.sections;
    return article.sections.map((section) => {
      const highlighted = section.content.replace(
        /<pre><code>([\s\S]*?)<\/code><\/pre>/g,
        (_match, code) => {
          const decoded = code
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"');
          const tokens = Prism.highlight(decoded, grammar, 'javascript');
          return `<pre><code class="language-javascript">${tokens}</code></pre>`;
        },
      );
      return { ...section, content: highlighted };
    });
  }, [article]);

  const toggleLightMode = () => {
    setLightMode((prev) => {
      const next = !prev;
      try { localStorage.setItem('article-light-mode', String(next)); } catch { /* noop */ }
      return next;
    });
  };

  // Track active section for TOC highlighting
  useEffect(() => {
    if (!article) return;
    const observers: IntersectionObserver[] = [];
    article.sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(section.id);
        },
        { threshold: 0.3, rootMargin: '-80px 0px -60% 0px' },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [article]);

  if (loading) return null;
  if (!article || !path) return <Navigate to="/learn" replace />;
  // API only returns published articles; static fallback checks published flag
  if (!article.published && !isArticlePublished(article.slug)) return <Navigate to="/learn" replace />;

  // Article JSON-LD structured data
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: 'Quanthop' },
    publisher: { '@type': 'Organization', name: 'Quanthop', logo: { '@type': 'ImageObject', url: 'https://quanthop.com/quanthop_logo.svg' } },
    url: `https://quanthop.com/learn/${category}/${slug}`,
    mainEntityOfPage: `https://quanthop.com/learn/${category}/${slug}`,
    ...(article.datePublished && { datePublished: article.datePublished }),
    ...(article.dateModified && { dateModified: article.dateModified }),
    ...(article.readTime && { timeRequired: `PT${parseInt(article.readTime, 10)}M` }),
  };

  // FAQ structured data (if article has FAQ items)
  const faqLd = article.faq?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  } : null;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Learn', item: 'https://quanthop.com/learn' },
      { '@type': 'ListItem', position: 2, name: path.title, item: `https://quanthop.com/learn/${category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://quanthop.com/learn/${category}/${slug}` },
    ],
  };

  // Prev/next articles within the category
  const articleIndex = path.articles.findIndex((a) => a.slug === article.slug);
  const prevArticle = articleIndex > 0 ? path.articles[articleIndex - 1] : null;
  const nextArticle =
    articleIndex < path.articles.length - 1
      ? path.articles[articleIndex + 1]
      : null;

  const related = getStaticRelated(article.slug);

  return (
    <div className={`min-h-screen transition-colors duration-300 selection:bg-[#9d4edd]/30 ${lightMode ? 'bg-[#f8f7f4] text-gray-900 selection:text-gray-900' : 'bg-[#0a0e14] text-white selection:text-white'}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      {faqLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />}
      <PublicNavBar />

      {/* Header */}
      <section
        ref={revHero.ref as React.RefObject<HTMLElement>}
        style={revHero.style}
        className="pt-32 pb-10 px-4 sm:px-6 max-w-4xl mx-auto"
      >
        {/* Breadcrumb */}
        <nav className={`flex items-center gap-2 text-[12px] mb-6 ${lightMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Link to="/learn" className={`transition-colors ${lightMode ? 'hover:text-gray-600' : 'hover:text-gray-300'}`}>
            Learn
          </Link>
          <span>/</span>
          <Link
            to={`/learn/${path.slug}`}
            className={`transition-colors ${lightMode ? 'hover:text-gray-600' : 'hover:text-gray-300'}`}
          >
            {path.title}
          </Link>
          <span>/</span>
          <span className={lightMode ? 'text-gray-600' : 'text-gray-400'}>{article.title}</span>
        </nav>

        <h1 className={`text-[24px] sm:text-[32px] font-semibold tracking-tight leading-tight mb-2 ${lightMode ? 'text-gray-900' : 'text-white'}`}>
          {article.title}
        </h1>

        {article.subtitle && (
          <p className={`text-[17px] sm:text-[20px] font-normal leading-snug mb-4 ${lightMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {article.subtitle}
          </p>
        )}

        <p className={`text-[15px] leading-[1.75] mb-5 max-w-2xl ${lightMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {article.description}
        </p>

        <div className="flex items-center gap-4 text-[12px] text-gray-500 font-mono">
          <span className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faClock} className="text-[10px]" />
            {article.readTime}
          </span>
          <span className={`flex items-center gap-1.5 ${difficultyColor[article.difficulty]}`}>
            <FontAwesomeIcon icon={faSignal} className="text-[10px]" />
            {difficultyLabel[article.difficulty]}
          </span>
          <button
            onClick={toggleLightMode}
            className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors text-[11px] ${lightMode ? 'border-gray-300 hover:border-gray-400 text-gray-500 hover:text-gray-700' : 'border-gray-700 hover:border-gray-500 text-gray-400 hover:text-gray-200'}`}
            aria-label={lightMode ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            <FontAwesomeIcon icon={lightMode ? faMoon : faSun} className="text-[10px]" />
            {lightMode ? 'Dark' : 'Light'}
          </button>
        </div>
      </section>

      {/* Content + TOC */}
      <section
        className="pb-16 px-4 sm:px-6 max-w-6xl mx-auto"
      >
        <div className="flex gap-10">
          {/* Table of Contents — desktop sidebar */}
          <aside className="hidden lg:block w-56 shrink-0">
            <nav className="sticky top-24">
              <span className={`text-[10px] font-mono tracking-[0.15em] uppercase block mb-3 ${lightMode ? 'text-gray-400' : 'text-gray-600'}`}>
                On this page
              </span>
              <ul className="space-y-2">
                {article.sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className={`block text-[12px] leading-snug transition-colors ${activeSection === section.id
                        ? 'text-[#9d4edd]'
                        : lightMode
                          ? 'text-gray-500 hover:text-gray-700'
                          : 'text-gray-500 hover:text-gray-300'
                        }`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Article body */}
          <article ref={articleRef} className="flex-1 min-w-0 max-w-[820px]">
            {highlightedSections.map((section) => {
              const MockComponent = section.mock ? mockRegistry[section.mock] : null;
              const isInline = section.layout === 'inline' && MockComponent;
              return (
                <div key={section.id} id={section.id} className="mb-16 scroll-mt-24">
                  <h2 className={`text-[18px] font-semibold mb-5 ${lightMode ? 'text-gray-900' : 'text-white'}`}>
                    {section.title}
                  </h2>
                  {isInline ? (
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                      <div className="w-full md:w-[340px] flex-shrink-0 mock-inline-wrapper">
                        <Suspense fallback={null}>
                          <MockComponent />
                        </Suspense>
                      </div>
                      <div
                        className={`flex-1 min-w-0 article-content text-[15px] leading-[1.75] ${lightMode ? 'article-content-light text-[#2d2d3a]' : 'text-[#d8dce3]'}`}
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        className={`article-content text-[15px] leading-[1.75] ${lightMode ? 'article-content-light text-[#2d2d3a]' : 'text-[#d8dce3]'}`}
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                      {MockComponent && (
                        <Suspense fallback={null}>
                          <MockComponent />
                        </Suspense>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </article>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={revCta.ref as React.RefObject<HTMLElement>}
        style={revCta.style}
        className="pb-12 px-4 sm:px-6 max-w-4xl mx-auto"
      >
        <div className={`border rounded-xl p-6 text-center ${lightMode ? 'border-gray-200 bg-gray-100/60' : 'border-gray-800 bg-gray-900/30'}`}>
          <Link
            to={article.cta.href}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-[14px] font-medium transition-colors"
          >
            {article.cta.text}
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="pb-12 px-4 sm:px-6 max-w-4xl mx-auto">
          <h3 className={`text-[14px] font-semibold mb-4 ${lightMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Related articles
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/learn/${r.category}/${r.slug}`}
                className={`group border rounded-lg p-4 transition-colors ${lightMode ? 'border-gray-200 hover:border-gray-300 bg-white/60' : 'border-gray-800 hover:border-gray-700 bg-gray-900/20'}`}
              >
                <h4 className={`text-[14px] font-semibold group-hover:text-[#9d4edd] transition-colors mb-1 ${lightMode ? 'text-gray-800' : 'text-white'}`}>
                  {r.title}
                </h4>
                <p className={`text-[12px] line-clamp-2 ${lightMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {r.description}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Prev / Next navigation */}
      <section className={`pb-16 px-4 sm:px-6 max-w-4xl mx-auto border-t pt-8 ${lightMode ? 'border-gray-200' : 'border-gray-800/40'}`}>
        <div className="flex justify-between items-start">
          {prevArticle ? (
            <Link
              to={`/learn/${path.slug}/${prevArticle.slug}`}
              className="group flex flex-col items-start"
            >
              <span className={`text-[11px] mb-1 flex items-center gap-1 ${lightMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <FontAwesomeIcon icon={faArrowLeft} className="text-[9px]" />
                Previous
              </span>
              <span className={`text-[13px] transition-colors ${lightMode ? 'text-gray-500 group-hover:text-gray-900' : 'text-gray-400 group-hover:text-white'}`}>
                {prevArticle.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
          {nextArticle ? (
            <Link
              to={`/learn/${path.slug}/${nextArticle.slug}`}
              className="group flex flex-col items-end"
            >
              <span className={`text-[11px] mb-1 flex items-center gap-1 ${lightMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Next
                <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
              </span>
              <span className={`text-[13px] transition-colors ${lightMode ? 'text-gray-500 group-hover:text-gray-900' : 'text-gray-400 group-hover:text-white'}`}>
                {nextArticle.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </section>

      {/* Back to hub — internal link for SEO */}
      <section className="pb-24 px-4 sm:px-6 max-w-4xl mx-auto text-center">
        <Link
          to="/learn"
          className={`text-[13px] transition-colors ${lightMode ? 'text-gray-400 hover:text-gray-600' : 'text-gray-500 hover:text-gray-300'}`}
        >
          Browse all learning paths
          <FontAwesomeIcon icon={faArrowRight} className="text-[10px] ml-1.5" />
        </Link>
      </section>

      <PublicFooter />
    </div>
  );
}
